import "./OperazioniHr.css"

import FiltroCategorie from "../../components/FiltroCategorie/FiltroCategorie";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import TrendPeriodo from "./TrendPeriodo/TrendPeriodo";
import DettaglioOper from "./DettaglioOper/DettaglioOper";
import DettaglioComm from "./DettaglioComm/DettaglioComm";

import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Select, { components } from "react-select";
import { MOCKED_OPERAZIONI_HR_CATEGORIES } from "../../mocks/MockedCategories";

// Componente custom per mostrare numero elementi selezionati
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    // Mostra solo i primi 2 elementi + un contatore
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

// Funzione helper per estrarre opzioni uniche dai dati
const getUniqueOptions = (data, field) => {
    const uniqueValues = [...new Set(data.map(item => item[field]))];
    return uniqueValues.map(value => ({ value, label: value }));
};

// Funzione helper per formattare la data
const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

// Funzione helper per convertire da formato month (yyyy-MM) al primo giorno del mese
const monthToFirstDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    return `${year}-${month}-01`;
};

// Funzione helper per convertire da formato month (yyyy-MM) all'ultimo giorno del mese
const monthToLastDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
};

// Funzione helper per convertire da formato date (yyyy-mm-dd) a formato month (yyyy-MM)
const dateToMonth = (dateValue) => {
    if (!dateValue) return '';
    const [year, month] = dateValue.split('-');
    return `${year}-${month}`;
};

// Helper per date di default
const getDefaultDateRange = () => {
    const today = new Date();
    const lastDayCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDay12MonthsAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);

    return {
        from: formatDate(firstDay12MonthsAgo),
        to: formatDate(lastDayCurrentMonth),
    };
};

const defaultDates = getDefaultDateRange();

/**
 * Trasforma i dati già aggregati dal BE al formato necessario per il FE
 * I dati arrivano già raggruppati per operatore, mese, anno e giustificativo
 * (aggregazione mensile, non giornaliera, per performance)
 */
// Trasforma i dati DB nel formato frontend per DettaglioComm
// filterOptions serve per il lookup: rapporto_lavoro_id → nome contratto (al posto del JOIN rimosso)
const mapDettaglioCommData = (dbData, filterOptions) => {
    const mesiMap = {
        1: 'Gennaio', 2: 'Febbraio', 3: 'Marzo', 4: 'Aprile',
        5: 'Maggio', 6: 'Giugno', 7: 'Luglio', 8: 'Agosto',
        9: 'Settembre', 10: 'Ottobre', 11: 'Novembre', 12: 'Dicembre'
    };

    // Mappa per lookup veloce: rapporto_lavoro_id → nome contratto
    const contrattoMap = {};
    if (filterOptions?.dettagliContratto) {
        filterOptions.dettagliContratto.forEach(dc => {
            // value può contenere più id separati da virgola (es. "1,2")
            const ids = String(dc.value).split(',');
            ids.forEach(id => {
                contrattoMap[id.trim()] = dc.label;
            });
        });
    }

    return dbData.map((item, index) => {
        const mese = mesiMap[item.mese];
        const anno = item.anno;
        const trimestre = `Trim ${Math.ceil(item.mese / 3)} - ${anno}`;

        return {
            id: index + 1,
            operatore: `Operatore ${item.anagrafica_operatore_id}`,
            settore: item.id_settore || "N/D",
            tipoContratto: item.rapporto_lavoro_id || "N/D",
            // Lookup in JS: usa la mappa costruita dai filtri invece del JOIN 
            dettaglioContratto: contrattoMap[String(item.rapporto_lavoro_id)] || "N/D",
            giustificativi: item.giustificativo_cat || "N/D",
            commessa: item.id_commessa || "N/D",
            attivitaLivello1: item.attivita_livello_1 || "N/D",
            attivitaLivello2: item.attivita_livello_2 || "N/D",
            mese: mese,
            trimestre: trimestre,
            anno: anno,
            oreLavorate: Number((item.min_lavorati / 60).toFixed(1)),
            oreNonLavorate: Number((item.min_non_lavorati / 60).toFixed(1)),
            oreDaContratto: Number((item.min_da_contratto / 60).toFixed(1)),
            oreDaTurno: Number((item.min_da_turno / 60).toFixed(1))
        };
    });
};

export const OperazioniHr = () => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const navigate = useNavigate();

    // Dati per chart e tabelle (null = non ancora caricato, [] = caricato ma vuoto)
    const [unfilteredHrData, setUnfilteredHrData] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        settori: [],
        operatori: [],
        tipiContratto: [],
        dettagliContratto: [],
        giustificativi: [],
        commesse: [],
        attivitaLivello1: [],
        attivitaLivello2: []
    });

    // Stato per UI
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [loading, setLoading] = useState(false);


    // Stati per i filtri
    const [selectedTrimestri, setSelectedTrimestri] = useState([]);
    const [selectedSettori, setSelectedSettori] = useState([]);
    const [selectedOperatori, setSelectedOperatori] = useState([]);
    const [selectedTipiContratto, setSelectedTipiContratto] = useState([]);
    const [selectedDettagliContratto, setSelectedDettagliContratto] = useState([]);
    const [selectedGiustificativi, setSelectedGiustificativi] = useState([]);
    const [selectedCommesse, setSelectedCommesse] = useState([]);

    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);

    // Ref per cancellare richieste API precedenti (evita race condition)
    const abortControllerRef = useRef(null);
    // Ref per evitare doppia chiamata da StrictMode di React in development
    const isLoadingFilters = useRef(false);
    const isLoadingData = useRef(false);

    // Cache per evitare reload se filtri non cambiano
    const dataCache = useRef({
        'Trend Periodo': { data: null, filters: null },
        'Dettaglio Operatori': { data: null, filters: null },
        'Dettaglio Commesse': { data: null, filters: null }
    });

    // Helper per creare hash dei filtri correnti
    const getCurrentFiltersHash = useCallback(() => {
        return JSON.stringify({
            fromDate,
            toDate,
            settori: selectedSettori.map(s => s.value).sort(),
            operatori: selectedOperatori.map(o => o.value).sort(),
            tipiContratto: selectedTipiContratto.map(t => t.value).sort(),
            dettagliContratto: selectedDettagliContratto.map(d => d.value).sort(),
            giustificativi: selectedGiustificativi.map(g => g.value).sort(),
            commesse: selectedCommesse.map(c => c.value).sort()
        });
    }, [fromDate, toDate, selectedSettori, selectedOperatori, selectedTipiContratto, selectedDettagliContratto, selectedGiustificativi, selectedCommesse]);

    // Handler per la selezione del mese di inizio
    const handleFromMonthChange = (e) => {
        const monthValue = e.target.value;
        const firstDay = monthToFirstDay(monthValue);
        setFromDate(firstDay);
    };

    // Handler per la selezione del mese di fine
    const handleToMonthChange = (e) => {
        const monthValue = e.target.value;
        const lastDay = monthToLastDay(monthValue);
        setToDate(lastDay);
    };

    // Memoizza le opzioni dei filtri
    const FILTER_OPTIONS = useMemo(() => {
        // unfilteredHrData può essere un array (Trend Periodo) o un oggetto {all, filtered} (Dettaglio Oper)
        const dataForFilters = Array.isArray(unfilteredHrData) ? unfilteredHrData : [];

        return {
            trimestri: getUniqueOptions(dataForFilters, 'trimestre').sort((a, b) => a.value.localeCompare(b.value)),
            settori: [...filterOptions.settori].sort((a, b) => a.value.localeCompare(b.value)),
            operatori: [...filterOptions.operatori].sort((a, b) => a.value.localeCompare(b.value)),
            tipiContratto: [...filterOptions.tipiContratto].sort((a, b) => a.value.localeCompare(b.value)),
            dettagliContratto: [...filterOptions.dettagliContratto].sort((a, b) => a.value.localeCompare(b.value)),
            giustificativi: [...filterOptions.giustificativi].sort((a, b) => a.value.localeCompare(b.value)),
            commesse: [...filterOptions.commesse].sort((a, b) => a.value.localeCompare(b.value))
        };
    }, [unfilteredHrData, filterOptions]);

    /* CHIAMATE API */
    const getFilters = useCallback(async () => {
        // Evita doppia chiamata da StrictMode in development
        if (isLoadingFilters.current) return;
        isLoadingFilters.current = true;

        setLoading(true)
        try {
            const res = await axios.get(`${API_URL}/operation-HR/trendPeriodo/getAllFilters`);

            const transformedFilters = {
                settori: res.data.settori.map(s => ({ value: s.id, label: s.valore })),
                operatori: res.data.operatori.map(o => ({ value: o.id, label: o.valore })),
                tipiContratto: res.data.tipiContratto.map(tc => ({ value: tc.id, label: tc.valore })),
                dettagliContratto: res.data.dettagliContratto.map(dc => ({ value: dc.id, label: dc.valore })),
                giustificativi: res.data.giustificativi.map(g => ({ value: g.valore, label: g.valore })),
                commesse: res.data.commesse.map(c => ({ value: c.id, label: c.valore }))
            };

            setFilterOptions(transformedFilters);
            setLoading(false)
        } catch (error) {
            console.error("Errore durante il caricamento dei filtri:", error);
            setLoading(false)
        } finally {
            isLoadingFilters.current = false;
        }
    }, [API_URL]);

    const getAllTrendPeriodo = useCallback(async (forceReload = false) => {
        // Evita doppia chiamata da StrictMode in development
        if (isLoadingData.current) return;

        const currentFilters = getCurrentFiltersHash();
        const cacheKey = 'Trend Periodo';

        // Se i filtri non sono cambiati e abbiamo dati in cache, usa la cache
        if (!forceReload && dataCache.current[cacheKey].filters === currentFilters && dataCache.current[cacheKey].data) {
            setUnfilteredHrData(dataCache.current[cacheKey].data);
            setLoading(false);
            return;
        }

        // Cancella eventuali richieste precedenti per evitare race condition
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        isLoadingData.current = true;
        setLoading(true);
        try {
            const filterParams = {
                dataInizio: fromDate,
                dataFine: toDate
            };

            if (selectedSettori.length > 0) {
                filterParams.settori = selectedSettori.map(s => s.value);
            }
            if (selectedOperatori.length > 0) {
                filterParams.operatori = selectedOperatori.map(o => o.value);
            }
            if (selectedTipiContratto.length > 0) {
                filterParams.tipiContratto = selectedTipiContratto.map(tc => tc.value);
            }
            if (selectedDettagliContratto.length > 0) {
                filterParams.dettagliContratto = selectedDettagliContratto.map(dc => dc.value);
            }
            if (selectedGiustificativi.length > 0) {
                filterParams.giustificativi = selectedGiustificativi.map(g => g.value);
            }
            if (selectedCommesse.length > 0) {
                filterParams.commesse = selectedCommesse.map(c => c.value);
            }

            const res = await axios.get(`${API_URL}/operation-HR/trendPeriodo/getAll`, {
                params: filterParams,
                signal
            });

            // Trasforma i dati aggregati (solo mese/anno + metriche, no dimensioni)
            const mesiMap = {
                1: 'Gennaio', 2: 'Febbraio', 3: 'Marzo', 4: 'Aprile',
                5: 'Maggio', 6: 'Giugno', 7: 'Luglio', 8: 'Agosto',
                9: 'Settembre', 10: 'Ottobre', 11: 'Novembre', 12: 'Dicembre'
            };
            const transformedData = res.data.map((item, index) => ({
                id: index + 1,
                mese: mesiMap[item.mese],
                trimestre: `Trim ${Math.ceil(item.mese / 3)} - ${item.anno}`,
                anno: item.anno,
                oreLavorate: Number((item.min_lavorati / 60).toFixed(1)),
                oreNonLavorate: Number((item.min_non_lavorati / 60).toFixed(1)),
                oreDaContratto: Number((item.min_da_contratto / 60).toFixed(1))
            }));

            // Salva in cache
            dataCache.current[cacheKey] = { data: transformedData, filters: currentFilters };

            setUnfilteredHrData(transformedData);
        } catch (error) {
            // Ignora errori di abort (sono intenzionali)
            if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
                return;
            }
            console.error("Errore durante il caricamento dei dati HR:", error);
            setUnfilteredHrData([]);
        } finally {
            isLoadingData.current = false;
            setLoading(false);
        }
    }, [API_URL, fromDate, toDate, selectedSettori, selectedOperatori, selectedTipiContratto, selectedDettagliContratto, selectedGiustificativi, selectedCommesse, getCurrentFiltersHash]);

    const getAllDettaglioOper = useCallback(async (forceReload = false) => {
        // Evita doppia chiamata da StrictMode in development
        if (isLoadingData.current) return;

        const currentFilters = getCurrentFiltersHash();
        const cacheKey = 'Dettaglio Operatori';

        // Se i filtri non sono cambiati e abbiamo dati in cache, usa la cache
        if (!forceReload && dataCache.current[cacheKey].filters === currentFilters && dataCache.current[cacheKey].data) {
            setUnfilteredHrData(dataCache.current[cacheKey].data);
            setLoading(false);
            return;
        }

        // Cancella eventuali richieste precedenti per evitare race condition
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        isLoadingData.current = true;
        setLoading(true);
        try {
            const filterParams = {
                dataInizio: fromDate,
                dataFine: toDate
            };

            if (selectedSettori.length > 0) {
                filterParams.settori = selectedSettori.map(s => s.value);
            }
            if (selectedOperatori.length > 0) {
                filterParams.operatori = selectedOperatori.map(o => o.value);
            }
            if (selectedTipiContratto.length > 0) {
                filterParams.tipiContratto = selectedTipiContratto.map(tc => tc.value);
            }
            if (selectedDettagliContratto.length > 0) {
                filterParams.dettagliContratto = selectedDettagliContratto.map(dc => dc.value);
            }
            if (selectedGiustificativi.length > 0) {
                filterParams.giustificativi = selectedGiustificativi.map(g => g.value);
            }
            if (selectedCommesse.length > 0) {
                filterParams.commesse = selectedCommesse.map(c => c.value);
            }

            const res = await axios.get(`${API_URL}/operation-HR/dettaglioOper/getAll`, {
                params: filterParams,
                signal
            });

            // Salva in cache
            dataCache.current[cacheKey] = { data: res.data, filters: currentFilters };

            // La risposta contiene due array: all (tutti) e filtered (senza rapporto_lavoro_id === 10)
            setUnfilteredHrData(res.data);

        } catch (error) {
            // Ignora errori di abort (sono intenzionali)
            if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
                return;
            }
            console.error("Errore durante il caricamento dei dati Dettaglio Operatori:", error);
            setUnfilteredHrData({ all: [], filtered: [] });
        } finally {
            isLoadingData.current = false;
            setLoading(false);
        }
    }, [API_URL, fromDate, toDate, selectedSettori, selectedOperatori, selectedTipiContratto, selectedDettagliContratto, selectedGiustificativi, selectedCommesse, getCurrentFiltersHash]);

    const getAllDettaglioComm = useCallback(async (forceReload = false) => {
        // Evita doppia chiamata da StrictMode in development
        if (isLoadingData.current) return;

        const currentFilters = getCurrentFiltersHash();
        const cacheKey = 'Dettaglio Commesse';

        // Se i filtri non sono cambiati e abbiamo dati in cache, usa la cache
        if (!forceReload && dataCache.current[cacheKey].filters === currentFilters && dataCache.current[cacheKey].data) {
            setUnfilteredHrData(dataCache.current[cacheKey].data);
            setLoading(false);
            return;
        }

        // Cancella eventuali richieste precedenti per evitare race condition
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        isLoadingData.current = true;
        setLoading(true);
        try {
            const filterParams = {
                dataInizio: fromDate,
                dataFine: toDate
            };

            if (selectedSettori.length > 0) {
                filterParams.settori = selectedSettori.map(s => s.value);
            }
            if (selectedOperatori.length > 0) {
                filterParams.operatori = selectedOperatori.map(o => o.value);
            }
            if (selectedTipiContratto.length > 0) {
                filterParams.tipiContratto = selectedTipiContratto.map(tc => tc.value);
            }
            if (selectedDettagliContratto.length > 0) {
                filterParams.dettagliContratto = selectedDettagliContratto.map(dc => dc.value);
            }
            if (selectedGiustificativi.length > 0) {
                filterParams.giustificativi = selectedGiustificativi.map(g => g.value);
            }
            if (selectedCommesse.length > 0) {
                filterParams.commesse = selectedCommesse.map(c => c.value);
            }

            const res = await axios.get(`${API_URL}/operation-HR/dettaglioComm/getAll`, {
                params: filterParams,
                signal
            });

            // Trasforma i dati: il nome contratto viene risolto in JS via filterOptions
            const transformedData = mapDettaglioCommData(res.data, filterOptions);

            // Salva in cache
            dataCache.current[cacheKey] = { data: transformedData, filters: currentFilters };

            setUnfilteredHrData(transformedData);
        } catch (error) {
            // Ignora errori di abort (sono intenzionali)
            if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
                return;
            }
            console.error("Errore durante il caricamento dei dati HR per Dettaglio Commesse:", error);
            setUnfilteredHrData([]);
        } finally {
            isLoadingData.current = false;
            setLoading(false);
        }
    }, [API_URL, fromDate, toDate, selectedSettori, selectedOperatori, selectedTipiContratto, selectedDettagliContratto, selectedGiustificativi, selectedCommesse, filterOptions, getCurrentFiltersHash]);

    useEffect(() => {
        getFilters();
    }, [getFilters]);

    // Handlers
    const handleCategorySelect = useCallback(async (category) => {
        setSelectedCategory(category);
        setLoading(true); // Mostra loader immediatamente
        if (category === "Trend Periodo") {
            getAllTrendPeriodo();
        } else if (category === "Dettaglio Operatori") {
            getAllDettaglioOper();
        } else if (category === "Dettaglio Commesse") {
            getAllDettaglioComm()
        }
    }, [getAllTrendPeriodo, getAllDettaglioOper, getAllDettaglioComm]);

    const handlePanelToggle = useCallback(() => {
        setShowFiltersPanel(prev => !prev);
    }, []);

    const applyFilter = useCallback(() => {
        // Forza il reload perché l'utente ha cliccato "Applica"
        if (selectedCategory === "Trend Periodo") {
            getAllTrendPeriodo(true);
        } else if (selectedCategory === "Dettaglio Operatori") {
            getAllDettaglioOper(true);
        } else if (selectedCategory === "Dettaglio Commesse") {
            getAllDettaglioComm(true);
        }
    }, [selectedCategory, getAllTrendPeriodo, getAllDettaglioOper, getAllDettaglioComm]);

    const handleFilterSubmit = useCallback((e) => {
        e.preventDefault();
        applyFilter();
    }, [applyFilter]);

    const handleReset = useCallback(() => {
        const defaultDates = getDefaultDateRange();
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);

        setSelectedTrimestri([]);
        setSelectedSettori([]);
        setSelectedOperatori([]);
        setSelectedTipiContratto([]);
        setSelectedDettagliContratto([]);
        setSelectedGiustificativi([]);
        setSelectedCommesse([]);

        // Invalida la cache per tutte le tab
        dataCache.current = {
            'Trend Periodo': { data: null, filters: null },
            'Dettaglio Operatori': { data: null, filters: null },
            'Dettaglio Commesse': { data: null, filters: null }
        };

        // Forza il reload dopo il reset
        if (selectedCategory === "Trend Periodo") {
            getAllTrendPeriodo(true);
        } else if (selectedCategory === "Dettaglio Operatori") {
            getAllDettaglioOper(true);
        } else if (selectedCategory === "Dettaglio Commesse") {
            getAllDettaglioComm(true);
        }
    }, [selectedCategory, getAllTrendPeriodo, getAllDettaglioOper, getAllDettaglioComm]);

    return (
        <div className="operazioni-hr-page">
            <header className="oper-hr-header">
                <div className="oper-hr-header-content">
                    <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="oper-hr-header-title">
                        <div className="oper-hr-title-row">
                            <h1>Operations HR</h1>
                            <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                <span>i</span>
                            </button>
                        </div>
                        <p className="oper-hr-subtitle">Visualizzazione delle ore lavorative dipendenti</p>
                    </div>
                </div>
            </header>
            {/* Modal Info */}
            {showInfoModal && (
                <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="oper-hr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="oper-hr-modal-header">
                            <div className="oper-hr-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="oper-hr-modal-body">
                            <div className="oper-hr-modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina permette di analizzare le ore lavorative dei dipendenti, visualizzare
                                    trend temporali e monitorare le performance in base a diversi criteri. I dati vengono
                                    aggregati per mese, trimestre e anno con calcolo automatico di KPI come ore straordinarie
                                    e percentuali di ore non lavorate.
                                </p>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Come utilizzarla?</h4>
                                <ul>
                                    <li><strong>Filtri:</strong> Applica filtri per periodo, settore, operatore, tipo contratto e giustificativi</li>
                                    <li><strong>Trend Periodo:</strong> Visualizza grafici interattivi e tabelle riassuntive delle ore lavorate</li>
                                    <li><strong>Viste Multiple:</strong> Passa tra vista mensile (M), trimestrale (T) e annuale (A)</li>
                                    <li><strong>Grafici Interattivi:</strong> Clicca sulle barre del grafico per filtrare i dati della tabella</li>
                                    <li><strong>Categorie:</strong> Seleziona tra Trend Periodo, Dettaglio Operatori e Dettaglio Commesse</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="oper-hr-main">

                {/* Bottone Mostra Filtri */}
                <div className="oper-hr-actions">
                    <button
                        type="button"
                        className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                        onClick={handlePanelToggle}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                    </button>
                </div>

                {/* Filtri Panel */}
                {showFiltersPanel && (
                    <div className="oper-hr-panel">
                        <form className="oper-hr-filters-form" onSubmit={handleFilterSubmit}>
                            <div className="oper-hr-filters-grid">
                                <div className="oper-hr-filter-item">
                                    <label>Mese Inizio</label>
                                    <MonthPickerModal
                                        value={dateToMonth(fromDate)}
                                        onChange={handleFromMonthChange}
                                        max={dateToMonth(toDate) || undefined}
                                        label="Mese Inizio"
                                    />
                                </div>

                                <div className="oper-hr-filter-item">
                                    <label>Mese Fine</label>
                                    <MonthPickerModal
                                        value={dateToMonth(toDate)}
                                        onChange={handleToMonthChange}
                                        min={dateToMonth(fromDate) || undefined}
                                        label="Mese Fine"
                                    />
                                </div>

                                <div className="oper-hr-filter-item">
                                    <label>Settore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedSettori}
                                        options={FILTER_OPTIONS.settori}
                                        onChange={setSelectedSettori}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-item oper-hr-filter-wide">
                                    <label>Commessa</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedCommesse}
                                        options={FILTER_OPTIONS.commesse}
                                        onChange={setSelectedCommesse}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-item oper-hr-filter-wide">
                                    <label>Operatore</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedOperatori}
                                        options={FILTER_OPTIONS.operatori}
                                        onChange={setSelectedOperatori}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-item oper-hr-filter-wide">
                                    <label>Tipo Contratto</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedTipiContratto}
                                        options={FILTER_OPTIONS.tipiContratto}
                                        onChange={setSelectedTipiContratto}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-item oper-hr-filter-wide">
                                    <label>Dettaglio Contratto</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedDettagliContratto}
                                        options={FILTER_OPTIONS.dettagliContratto}
                                        onChange={setSelectedDettagliContratto}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-item oper-hr-filter-wide">
                                    <label>Giustificativo</label>
                                    <Select
                                        classNamePrefix="oper-hr-select"
                                        placeholder="Tutti"
                                        value={selectedGiustificativi}
                                        options={FILTER_OPTIONS.giustificativi}
                                        onChange={setSelectedGiustificativi}
                                        isMulti
                                        components={{ MultiValue: MultiValueContainer }}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                    />
                                </div>

                                <div className="oper-hr-filter-actions">
                                    <button type="submit" className="oper-hr-btn-primary">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Applica
                                    </button>
                                    <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                            <path d="M3 3v5h5"></path>
                                        </svg>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Filters Section */}
                <div className="oper-hr-data-section">
                    <FiltroCategorie categories={MOCKED_OPERAZIONI_HR_CATEGORIES} filteredCategory={handleCategorySelect} customCss={"reportistica-section"} />

                    {/* Componente TrendPeriodo - Grafici e Tabelle */}
                    {selectedCategory === "Trend Periodo" &&
                        <TrendPeriodo
                            unfilteredHrData={unfilteredHrData}
                            loading={loading}
                            fromDate={fromDate}
                            toDate={toDate}
                            selectedTrimestri={selectedTrimestri}
                            selectedCategory={selectedCategory}
                        />
                    }
                    {selectedCategory === "Dettaglio Operatori" && (
                        <DettaglioOper
                            unfilteredHrData={unfilteredHrData}
                            loading={loading}
                            fromDate={fromDate}
                            toDate={toDate}
                            selectedCategory={selectedCategory}
                            filterOptions={filterOptions}
                        />
                    )}
                    {selectedCategory === "Dettaglio Commesse" &&
                        <DettaglioComm
                            unfilteredHrData={unfilteredHrData}
                            loading={loading}
                            fromDate={fromDate}
                            toDate={toDate}
                            selectedCategory={selectedCategory}
                            filterOptions={filterOptions}
                        />
                    }
                </div>
            </main>
        </div>
    )
}

export default OperazioniHr;